import {
  Analytics,
  getAnalytics,
  logEvent,
  setCurrentScreen,
  setUserId,
  setUserProperties,
} from 'firebase/analytics';
import { FirebaseApp, initializeApp } from 'firebase/app';
import { fetchAndActivate, getRemoteConfig, getValue, RemoteConfig } from 'firebase/remote-config';

// import { getPerformance } from 'firebase/performance';
import { EventProps, TrackerConfig, TrackerEvent, UserEvent } from '../../types/services';

import { trackEvents } from '../../config/events';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FB_API_KEY,
  authDomain: process.env.REACT_APP_FB_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FB_DATABASE_URL,
  projectId: process.env.REACT_APP_FB_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FB_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FB_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FB_APP_ID,
  measurementId: process.env.REACT_APP_FB_MEASUREMENT_ID,
};

type OurUserProps =
  | 'apc_user_country'
  | 'apc_user_id'
  | 'apc_user_email_status'
  | 'apc_user_last_login_date'
  | 'apc_user_has_cards'
  | 'apc_user_lang'
  | 'apc_user_cashout_enabled'
  | 'apc_user_invite_users'
  | 'apc_user_kyc_status'
  | 'apc_user_last_buy_date'
  | 'apc_user_last_buy_status'
  | 'apc_user_loqate'
  | 'apc_user_purchasers'
  | 'apc_user_refunds'
  | 'apc_user_registered'
  | 'apc_user_sign_up_date'
  | 'apc_user_source'
  | 'apc_user_status'
  | 'apc_user_whitelist'
  | 'firebase_last_notification';

type UserPropsObject = {
  [key in OurUserProps]: string | number | boolean;
};

type RemoteConfigParams = 'timeoutSMS';

class FirebaseTracker extends TrackerEvent {
  private app: FirebaseApp | null = null;
  private analytics: Analytics | null = null;
  private remoteConfig: RemoteConfig | null = null;

  valid = (key: keyof TrackerConfig) => {
    return trackEvents.firebase.enabled && trackEvents.firebase[key];
  };
  /**
   * Initialize Firebase intance
   * @param init boolean
   */
  init = async () => {
    if (this.valid('enabled')) {
      try {
        this.app = initializeApp(firebaseConfig);
        // REMOTE CONFIG
        this.remoteConfig = getRemoteConfig(this.app);
        this.remoteConfig.settings = {
          fetchTimeoutMillis: 60000,
          minimumFetchIntervalMillis: 3000,
        };
        this.remoteConfig.defaultConfig = {
          timeoutSMS: 60,
        };
        // LOAD REMOTE CONFIG
        await fetchAndActivate(this.remoteConfig);
        // LOAD PERFORMANCE
        // getPerformance(this.app);
        // LOAD ANALYTICS
        this.analytics = getAnalytics(this.app);
        super.init();
      } catch {}
    }
  };

  /**
   * Track event on firebase
   * @param eventName string
   * @param props { [key: string]: string | number | booolean }
   */
  trackEvent = (eventName: string, props: EventProps) => {
    if (this.inicialized && this.valid('event') && !!this.analytics) {
      logEvent(this.analytics, eventName, props);
    }
  };

  trackPageView = (page: string) => {
    if (this.inicialized && this.valid('enabled') && !!this.analytics) {
      setCurrentScreen(this.analytics, page);
    }
  };

  /**
   * Track user on Firebase
   * @param user UserEvent
   * @param newUser boolean
   */
  trackUser = (user: UserEvent, newUser = false) => {
    if (this.inicialized && this.valid('enabled') && !!this.analytics) {
      setUserId(this.analytics, user.id.toString());
      if (newUser) {
        this.trackNewUser(user);
      } else {
        this.setUserProps({ apc_user_last_login_date: new Date().toISOString().slice(0, 10) });
      }
    }
  };

  private trackNewUser(user: UserEvent) {
    const currentTime = new Date().toISOString().slice(0, 10);
    this.setUserProps({
      apc_user_id: user.id,
      apc_user_country: user.country,
      apc_user_registered: true,
      apc_user_lang: user.language,
      apc_user_has_cards: false,
      apc_user_invite_users: false,
      apc_user_purchasers: false,
      apc_user_refunds: false,
      apc_user_last_login_date: currentTime,
      apc_user_sign_up_date: currentTime,
    });
  }

  private setUserProps = (userProps: Partial<UserPropsObject>) => {
    try {
      if (!!this.analytics) {
        setUserProperties(this.analytics, userProps);
      }
    } catch {}
  };

  getRemoteConfig = (
    parameter: RemoteConfigParams,
    type: 'boolean' | 'number' | 'string' | 'object',
  ) => {
    try {
      if (this.inicialized && !!this.remoteConfig) {
        const value = getValue(this.remoteConfig, parameter);
        switch (type) {
          case 'boolean':
            return value.asBoolean();
          case 'number':
            return value.asNumber();
          case 'object':
            return JSON.parse(value.asString());
          default:
            return value.asString();
        }
      }
    } catch {}
  };

  getAllRemoteConfigs = () => {
    try {
      if (this.inicialized && !!this.remoteConfig) {
        fetchAndActivate(this.remoteConfig).then().catch();
      }
    } catch {}
  };
}

export default new FirebaseTracker();
